import { useState, useEffect } from 'react';
import { Loader2, ArrowLeft, Sparkles, Clock } from 'lucide-react';
import { DokladStatusBadge } from './FinanceDokladyInbox';
import DokladUploader from '../components/doklady/DokladUploader';

const SECTION_COLOR = '#3B82F6';

const fmtRelativeTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'právě teď';
    if (diff < 3600) return `před ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `před ${Math.floor(diff / 3600)} h`;
    if (diff < 86400 * 7) return `před ${Math.floor(diff / 86400)} d`;
    return d.toLocaleDateString('cs-CZ');
};

/**
 * Mobilní stránka „Nahrát doklad" — optimalizováno pro telefon na ruce.
 *
 * Vlastní upload (focení, výběr, staging seskupení do vícestránkového dokladu,
 * geo, poznámka, paralelní async upload) řeší sdílené jádro `DokladUploader`
 * (stejná komponenta jako desktop). Tato stránka doplňuje jen header a přehled
 * „mé nedávné uploady" s auto-refreshem, ať uživatel vidí, jak AI sken postupuje.
 */
export default function DokladyMobilUpload({ user, onNavigate }) {
    const [mojeUploady, setMojeUploady] = useState([]);
    const [loadingUploady, setLoadingUploady] = useState(true);

    // Načíst moje nedávné uploady (server filtruje per uzivatel automaticky pro L3)
    const loadMojeUploady = () => {
        setLoadingUploady(true);
        fetch('/api/finance/doklady/inbox?limit=20')
            .then(r => r.json())
            .then(data => { setMojeUploady(data.doklady || []); setLoadingUploady(false); })
            .catch(() => setLoadingUploady(false));
    };

    useEffect(() => {
        loadMojeUploady();
        const id = setInterval(loadMojeUploady, 30000); // ať uživatel vidí postup AI skenu
        return () => clearInterval(id);
    }, []);

    return (
        <div style={{ background: 'var(--surface-base)', minHeight: '100dvh', paddingBottom: 80 }}>
            {/* Header s back tlačítkem */}
            <div className="sticky top-0 z-10 flex items-center gap-3 px-4 py-3" style={{ background: 'var(--surface-base)', borderBottom: '1px solid var(--border-default)' }}>
                {onNavigate && (
                    <button onClick={() => onNavigate('prehled')} className="p-2 rounded-lg"
                        style={{ background: 'rgba(0,0,0,0.04)', color: 'var(--text-secondary)' }}>
                        <ArrowLeft size={18} />
                    </button>
                )}
                <div>
                    <h1 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>Nahrát doklad</h1>
                    <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                        Vyfoť účtenku, AI ji rozpozná a pošle ke schválení
                    </p>
                </div>
            </div>

            <div className="px-4 py-4 space-y-4 max-w-lg mx-auto">
                <DokladUploader
                    zdroj="mobil"
                    variant="page"
                    showGeo
                    showPoznamka
                    onUploaded={loadMojeUploady}
                />

                {/* Moje nedávné uploady */}
                <div>
                    <div className="text-xs font-medium mb-2 mt-6" style={{ color: 'var(--text-secondary)' }}>
                        Mé nedávné uploady
                    </div>
                    {loadingUploady && mojeUploady.length === 0 ? (
                        <div className="flex items-center justify-center py-6">
                            <Loader2 size={18} className="animate-spin" style={{ color: SECTION_COLOR }} />
                        </div>
                    ) : mojeUploady.length === 0 ? (
                        <div className="text-center py-8 text-sm" style={{ color: 'var(--text-tertiary)' }}>
                            <Clock size={20} className="mx-auto mb-2 opacity-50" />
                            Zatím jsi nenahrál žádný doklad
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {mojeUploady.map(d => (
                                <div key={d.id} className="rounded-xl p-3 flex items-center gap-3"
                                    style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-default)' }}>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <DokladStatusBadge stav={d.stav} />
                                            {d.ai_confidence_avg != null && (
                                                <span className="inline-flex items-center gap-0.5 text-[10px] font-mono" style={{ color: '#10b981' }}>
                                                    <Sparkles size={10} />
                                                    {Math.round(d.ai_confidence_avg * 100)}%
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-sm truncate" style={{ color: 'var(--text-primary)' }}>
                                            {d.dodavatel?.nazev || d.dodavatel || <span style={{ color: 'var(--text-tertiary)' }} className="italic">čeká na AI…</span>}
                                        </div>
                                        <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                            {d.castka_s_dph > 0 ? `${d.castka_s_dph} Kč · ` : ''}
                                            {fmtRelativeTime(d.created_at)}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
